import { useState } from "react";
import { motion } from "framer-motion";
import SceneShell from "../components/SceneShell";
import BeatPulse from "../components/BeatPulse";

/**
 * Scene · Social Week — live tool demo. Yellow / gold treatment to match
 * the SOCIAL WEEK brand wordmark. Static screenshot + descriptor copy.
 *
 * Drop /public/social-week-tool.png to replace the screenshot.
 */

const TOOL_SRC = "/social-week-tool.png";
const GOLD = "#e9a820";
const GOLD_DEEP = "#d4940a";

export default function SceneSocialWeekDemo() {
  const [imageOk, setImageOk] = useState(true);
  return (
    <SceneShell variant="ink" align="left" data-testid="scene-social-week-demo">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(ellipse at 25% 50%, rgba(212,148,10,0.18) 0%, transparent 55%)",
            "radial-gradient(ellipse at 75% 50%, rgba(233,168,32,0.22) 0%, transparent 55%)",
            "radial-gradient(ellipse at 25% 50%, rgba(212,148,10,0.18) 0%, transparent 55%)",
          ],
        }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative max-w-[1500px] mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-4">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-3 kn-eyebrow"
            style={{ color: GOLD }}
          >
            <BeatPulse size={9} label="Live demo" color={GOLD} />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="kn-display text-4xl sm:text-5xl lg:text-6xl text-white mt-8"
            data-testid="social-week-demo-title"
          >
            Social Week.
            <br />
            <span
              style={{
                background: `linear-gradient(135deg, ${GOLD_DEEP} 0%, ${GOLD} 50%, ${GOLD_DEEP} 100%)`,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                fontWeight: 900,
              }}
            >
              Watch it run.
            </span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-3"
            data-testid="social-week-demo-tags"
          >
            {["Just Listed", "Featured", "Sold", "Carousel", "Stories", "Grid"].map(
              (tag, i) => (
                <motion.span
                  key={tag}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.4 + i * 0.1, duration: 0.6 }}
                  className="px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-[0.12em]"
                  style={{
                    background: "rgba(233,168,32,0.14)",
                    border: `1px solid ${GOLD}55`,
                    color: GOLD,
                  }}
                >
                  {tag}
                </motion.span>
              )
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 0.7 }}
            className="mt-10 inline-flex items-center gap-3"
          >
            <span
              className="block w-10 h-px"
              style={{ background: GOLD }}
            />
            <span
              className="text-[11px] tracking-[0.32em] uppercase"
              style={{ color: "rgba(255,255,255,0.7)", fontWeight: 700 }}
            >
              One press · seven days · every channel
            </span>
          </motion.div>
        </div>

        <div className="lg:col-span-8 relative">
          <motion.figure
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-2xl overflow-hidden"
            style={{
              aspectRatio: "16 / 9",
              background: "rgba(255,255,255,0.03)",
              border: `1px solid ${GOLD}33`,
              boxShadow: `0 30px 80px -20px rgba(0,0,0,0.7), 0 0 60px ${GOLD}1f`,
            }}
            data-testid="social-week-demo-frame"
          >
            {imageOk ? (
              <img
                src={TOOL_SRC}
                alt="Social Week tool · NicheCom Social Week template editor"
                className="absolute inset-0 w-full h-full object-contain"
                onError={() => setImageOk(false)}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  className="text-[11px] tracking-[0.32em] uppercase"
                  style={{ color: "rgba(255,255,255,0.45)" }}
                >
                  Drop screenshot at /public/social-week-tool.png
                </div>
              </div>
            )}
          </motion.figure>
        </div>
      </div>
    </SceneShell>
  );
}
